import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isEqual } from 'lodash';
import {
    PickupLocation,
    PrepareScheduleCourierPickupData,
    ScheduleCourierPickupOrder,
    ScheduleCourierPickupPayload,
} from '../../../_core/interfaces';
import { ScheduleCourierPickUpData } from './schedule-courier-pick-up-data.interface';
import { ScheduleCourierPickUpFormGroup } from './schedule-courier-pick-up-form-group.interface';

@Injectable()
export class ScheduleCourierPickUpFormService {
    private readonly form = this.initializeScheduleCourierPickUpForm();
    private readonly initialData: ScheduleCourierPickUpData = {
        courierId: '',
        orders: [],
        numberOfTubes: 0,
        firstName: '',
        lastName: '',
        organizationId: '',
        groupId: '',
        pickupLocation: null,
    };

    getScheduleCourierForm(): FormGroup<ScheduleCourierPickUpFormGroup> {
        return this.form;
    }

    populateForm(courierPickupData: PrepareScheduleCourierPickupData) {
        this.form.setValue({
            courier: courierPickupData.courier,
            orderNames: [],
            numberOfTubes: 0,
            firstName: courierPickupData.firstName,
            lastName: courierPickupData.lastName,
            organization: courierPickupData.organization,
            group: courierPickupData.group,
            pickupLocation: null,
        });
    }

    private initializeScheduleCourierPickUpForm() {
        return new FormGroup<ScheduleCourierPickUpFormGroup>({
            courier: new FormControl<string>(
                { disabled: true, value: '' },
                { validators: [Validators.required], nonNullable: true, updateOn: 'change' }
            ),
            orderNames: new FormControl<ScheduleCourierPickupOrder[]>([], {
                validators: [Validators.required],
                nonNullable: true,
                updateOn: 'change',
            }),
            numberOfTubes: new FormControl<number>(
                { disabled: true, value: 0 },
                {
                    validators: [Validators.required],
                    nonNullable: true,
                    updateOn: 'change',
                }
            ),
            firstName: new FormControl<string>(
                { disabled: true, value: '' },
                { validators: [Validators.required], nonNullable: true, updateOn: 'change' }
            ),
            lastName: new FormControl<string>(
                { disabled: true, value: '' },
                { validators: [Validators.required], nonNullable: true, updateOn: 'change' }
            ),
            organization: new FormControl<string>(
                { disabled: true, value: '' },
                { validators: [Validators.required], nonNullable: true, updateOn: 'change' }
            ),
            group: new FormControl<string>(
                { disabled: true, value: '' },
                { validators: [Validators.required], nonNullable: true, updateOn: 'change' }
            ),
            pickupLocation: new FormControl<PickupLocation | null>(null, {
                validators: [Validators.required],
                nonNullable: true,
                updateOn: 'change',
            }),
        });
    }

    hasModifiedInitialGeneralSettings(): boolean {
        if (this.orderNames.value !== null && !isEqual(this.orderNames.value, this.initialData.orders)) return true;
        if (this.pickupLocation.value !== this.initialData.pickupLocation) return true;
        return false;
    }

    computePayload(): ScheduleCourierPickupPayload {
        return {
            orderIds: this.orderNames.value?.map(order => order.id) ?? [],
            pickupLocationId: this.pickupLocation.value?.id ?? null,
        };
    }

    resetForm(): void {
        this.form.reset();
    }

    // getters
    get courier(): FormControl<string | null> {
        return this.form.controls['courier'] as FormControl<string | null>;
    }

    get orderNames(): FormControl<ScheduleCourierPickupOrder[] | null> {
        return this.form.controls['orderNames'] as FormControl<ScheduleCourierPickupOrder[] | null>;
    }

    get numberOfTubes(): FormControl<number | null> {
        return this.form.controls['numberOfTubes'] as FormControl<number | null>;
    }

    get firstName(): FormControl<string | null> {
        return this.form.controls['firstName'] as FormControl<string | null>;
    }

    get lastName(): FormControl<string | null> {
        return this.form.controls['lastName'] as FormControl<string | null>;
    }

    get organization(): FormControl<string | null> {
        return this.form.controls['organization'] as FormControl<string | null>;
    }

    get group(): FormControl<string | null> {
        return this.form.controls['group'] as FormControl<string | null>;
    }

    get pickupLocation(): FormControl<PickupLocation | null> {
        return this.form.controls['pickupLocation'] as FormControl<PickupLocation | null>;
    }
}
